import { useEffect, useMemo, useState } from "react";
import { Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { FilterToolbar } from "@/components/layout/FilterToolbar";
import { portalPackagesApi } from "../api/packages";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatDateInTz } from "@/design-system/lib/format";
import type { PortalDealSummaryDto } from "../api/types";

// Post-Rollout PR 2 (PROB-13 F.2) — Deals & status tab rewritten onto
// the shared DataTable + FilterToolbar. Rows are deals the signed-in
// party is linked to as a counterparty (buyer / seller / borrower /
// business). Read-only: rows deep-link to the package detail (closest
// drill-in we have today; a dedicated portal-side deal detail surface
// is a fast-follow).

export function DealsTab() {
  const [rows, setRows] = useState<PortalDealSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalPackagesApi.listMyDeals();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load deals.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const term = search.trim().toLowerCase();
    return rows.filter((d) => {
      if (roleFilter !== "all" && d.counterpartyRole !== roleFilter) return false;
      if (!term) return true;
      return (
        d.dealNumber.toLowerCase().includes(term) ||
        (d.businessName ?? "").toLowerCase().includes(term)
      );
    });
  }, [rows, search, roleFilter]);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!rows) {
    return (
      <p className="text-small text-gray-mid" data-testid="deals-loading">
        Loading your deals…
      </p>
    );
  }

  const roles = Array.from(new Set(rows.map((d) => d.counterpartyRole)));

  return (
    <section aria-label="Deals" className="flex flex-col gap-space-4">
      <FilterToolbar
        growSearch
        searchInput={
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by deal # or business…"
            data-testid="deals-filter-search"
          />
        }
        filters={
          roles.length > 1
            ? [
                {
                  key: "role",
                  label: "Role",
                  control: (
                    <select
                      className="h-10 rounded-md border border-border-light px-space-2 text-small bg-white-pure"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      data-testid="deals-filter-role"
                    >
                      <option value="all">All roles</option>
                      {roles.map((r) => (
                        <option key={r} value={r}>
                          {prettyRole(r)}
                        </option>
                      ))}
                    </select>
                  ),
                },
              ]
            : []
        }
      />

      <DataTable<PortalDealSummaryDto>
        rows={filtered ?? []}
        rowKey={(d) => `${d.dealId}:${d.counterpartyRole}`}
        sortable
        defaultSort={{ key: "updated", dir: "desc" }}
        pagination={{ pageSize: 15 }}
        emptyMessage="No deals match those filters."
        columns={dealsColumns()}
      />
    </section>
  );
}

function dealsColumns(): Column<PortalDealSummaryDto>[] {
  return [
    {
      key: "deal",
      header: "Deal",
      sortable: true,
      sortAccessor: (d) => d.dealNumber,
      render: (d) => (
        <span className="text-small font-medium text-onyx" data-testid={`deal-row-${d.dealId}`}>
          {d.dealNumber}
          {d.businessName ? ` · ${d.businessName}` : ""}
        </span>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      sortable: true,
      sortAccessor: (d) => d.stage,
      render: (d) => (
        <span className="text-caption text-gray-mid">
          {prettyStage(d.stage)} · {prettySubState(d.subState)}
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      sortable: true,
      sortAccessor: (d) => d.counterpartyRole,
      render: (d) => (
        <span className="text-caption text-gray-mid">{prettyRole(d.counterpartyRole)}</span>
      ),
    },
    {
      key: "updated",
      header: "Last updated",
      sortable: true,
      sortAccessor: (d) => Date.parse(d.lastUpdatedAt),
      render: (d) => <LastUpdatedCell at={d.lastUpdatedAt} />,
    },
  ];
}

function LastUpdatedCell({ at }: { at: string }) {
  return <span className="text-caption text-gray-mid">{formatDateInTz(at, null)}</span>;
}

function prettyRole(role: string): string {
  switch (role) {
    case "Borrower":
      return "You're the borrower";
    case "Seller":
      return "You're the seller";
    case "Business":
      return "Your business";
    default:
      return role;
  }
}

function prettyStage(s: string): string {
  return s.replace(/PreApproval/g, "Pre-approval").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function prettySubState(s: string): string {
  return s.replace(/([a-z])([A-Z])/g, "$1 $2");
}
